import { type TimestampDeviceTZ, type TimestampUTC } from '@common/types/brandedTypes';
import type Register from '@common/types/models/Register';
import {
  type ProjectionValueType,
  type RegisterSpecialValuesState,
} from '@common/types/models/Register';

export interface RegisterResponse {
  registerId: number;
  registerIndex: number;
  registerName: string;
  registerValue: number;
  stringRegisterValue: string;
  timeStampUtc?: string | null;
  isComputedRegister: number;
  presentation: 'Slider' | 'Integer' | 'Enumeration' | 'Text' | 'Boolean';
  isReadOnly: boolean;
  minValue: number;
  maxValue: number;
  valueNames: {
    value: number;
    name: string;
    visible: boolean;
    confirmation: string;
    isReadonly: boolean;
  }[];
  falseText: string;
  trueText: string;
  step: number;
  modifier: number;
  disabledByLink: boolean;
  isInvisible: boolean;
  canBeShownInGraph: boolean;
  graphRegisterIndex: number;
  canBeShownInHistory: boolean;
  collapsible: boolean;
  groupId: number;
  groupOrder: number;
  groupName: string;
  groupParentId: number;
  groupItemId: number;
  groupItemOrder: number;
  unit: string;
  precision: number;
  specialValues?: { value: number; state: RegisterSpecialValuesState; }[];
}

export interface MappedRegistryGroup {
  id: number;
  name: string;
  visibilityId: number;
  order: number;
  children: MappedRegistryGroup[];
  items: Register[];
}

interface RegisterGroupItem {
  id: number;
  order: number;
  registerSpecificationId: number | null;
  stringRegisterSpecificationId: string | null;
  projectionId: number | null;
}

export interface RegistryGroup {
  id: number;
  name: string;
  visibilityId: number;
  order: number;
  children: RegistryGroup[] | null;
  items: RegisterGroupItem[];
}

export enum RegisterGroupVisibility {
  MostImportantRegisters = 2,
  DiagramTab = 3,
  ParametersTab = 4,
  Programs = 5,
  Commissioning = 6,
  CostOfPerformance = 7,
  EnergyMetrics = 8,
}

export enum DataHistoryResolution {
  All = 0,
  Minute = 1,
  Hour = 2,
  Day = 3,
  Month = 4,
  Year = 5,
  Week = 6,
}

export enum DataHistoryAggregation {
  None = 0,
  Avg = 1,
  Min = 2,
  Max = 3,
  First = 4,
  Last = 5,
  Count = 6,
  Sum = 7,
}

export interface DataHistoryItem {
  at: TimestampUTC;
  val: string | number;
}

export interface ResponseHistoryItem {
  at: TimestampDeviceTZ;
  val: string | number;
}

export interface SpecialValuesPeriodItem {
  at: TimestampDeviceTZ;
  vals: number[];
}

export interface DataHistoryResponse {
  period: ResponseHistoryItem[];
  specialValuesPeriod?: SpecialValuesPeriodItem[];
  prePeriod?: ResponseHistoryItem;
  postPeriod?: ResponseHistoryItem;
}

export enum Unit {
  NotSpecified = 10,
  DegreeCelsius = 20,
  Kelvin = 25,
  Percent = 30,
  Wh = 35,
  KWh = 40,
  DLiter = 50,
  Liter = 60,
  LiterPerMinute = 65,
  MWh = 70,
  Bar = 80,
  MilliBar = 85,
  Day = 90,
  Hour = 95,
  Minute = 100,
  Millisecond = 105,
  Voltage = 120,
  Amper = 130,
  Herz = 140,
  W = 145,
  KW = 150,
  CentsPerKilowatt = 160,
  CentsPerLitre = 170,
  GramsPerKilowatt = 180,
  SquareMeter = 190,
  KilowattPeak = 200,
  HourMinute = 210,
  MilliKelvin = 220,
  KelvinPerMinute = 230,
  m3 = 240,
  m3h = 250,
  Month = 260,
  Time = 270,
  Pascal = 280,
  Year = 290,
  KelvinPerDay = 300,
  Second = 310,
  CountPerDay = 320,
  Thousandm3 = 330,
  PercentPerKelvin = 340,
  PascalSecondm3 = 350,
  PascalPerm3 = 360,
  KilowattHourPerKelvin = 370,
  MilliKelvinPerWatt = 380,
  RevolutionsPerMinute = 390,
}

enum Presentation {
  Integer = 10,
  Boolean = 20,
  Slider = 30,
  Enumeration = 40,
  CommandButton = 45,
  Flags = 50,
  Hidden = 60,
  StatusFlags = 70,
  MultipleStatusFlags = 71,
  StatusFlagsTwoCol = 80,
  StatusFlagsTextInd = 90,
  ActiveOrInactive = 100,
  IntMappedToText = 110,
  Text = 120,
}

export interface ProjectionItem {
  key: string;
  registerIndex?: number | null;
  value: string;
  type: ProjectionValueType;
  specialValue: number | null;
  minDisplay?: number;
  maxDisplay?: number;
  scale?: number;
  precision?: number;
  unit?: Unit;
  presentation: Presentation;
}

export interface ProjectionField {
  id: number;
  name: string;
  order: number;
  items: ProjectionItem[];
  isReadOnly: boolean;
}

export interface ProjectionResponse {
  id: number;
  name: string;
  fields: ProjectionField[];
}

export interface ProjectionsGroup {
  name: string;
  items: ProjectionResponse[];
}

interface ProjectionUpdateItem {
  key: string;
  value: string | null;
}

// projectionFieldId is not really optional, but we cannot fill this field properly
// only if the original program doesn't contain some field that we expect to be able
// to change. In that case we don't know the proper projectionFieldId anyway, so we
// can as well leave this field empty and let the subsequent request fail.
export interface ProjectionUpdateField {
  projectionFieldId?: number;
  items: ProjectionUpdateItem[];
}

export interface ProjectionUpdateRequest {
  clientUuid?: string;
  fields: ProjectionUpdateField[];
}
